import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
  toastFailure,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const { handleGraphqlError } = useHandleError('taxoffice');
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateTaxoffice] = useMutation(gqlUpdate);
  const [addNewTaxoffice] = useMutation(gqlAdd);
  const [deleteTaxoffice] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Taxoffice'), { id: row.id }),
        state: {},
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      //   EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
        meta: { allowEdit: false },
      },
      {
        id: 'name',
        header: t('Επωνυμία Δ.Ο.Υ'),
        columnName: t('Επωνυμία Δ.Ο.Υ'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'address',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'address',
        orderByField: 'address',
        cell: TableCellV8,
        size: 60,
        meta: { type: 'text' },
      },
      {
        id: 'city',
        header: t('Πόλη'),
        columnName: t('Πόλη'),
        accessorKey: 'city',
        orderByField: 'city',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },

      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      lastDateUserColumn(t),
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="taxoffices-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
              collectorId: appStore.isCollector ? appStore.associatedId : undefined,
            };
          }}
          onBeforeSaveRow={async (state, row, changes) => {
            if (row?.isNotActive !== undefined && row.isNotActive === true) return true;
            return true;
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            let id = row.id;
            const values =
              tableStore.state === TableDataState.insert ? { ...row } : { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewTaxoffice({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                  toastFailure(
                    <>
                      Αποτυχία Προσθήκης Εγγραφής
                      <div className="mt-1">{reason.message}</div>
                    </>,
                  );
                },
              );
              if (!res) return false;
              toastSuccess('Επιτυχής Προσθήκη');
              id = res.data.addTaxoffice.id;
            } else if (tableStore.state === TableDataState.edit) {
              const res = await updateTaxoffice({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow update error reason', reason);
                },
              );
              if (!res) return false;
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε την Εγγραφή <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteTaxoffice({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false;
            }
            refetch();
            toastSuccess('Διαγράφηκε');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});

export default DataGrid;
